import { activityRules, workoutRules, type WorkoutIntensity } from "../../backend/src/rules/activity";
import { localISODate } from "./water-repository";

export interface ActivityEntry {
  workouts?: Workout[];
  date: string;
  steps: number;
  workoutCalories: number;
  walkingCalories: number | null;
  walkingCaloriesInput?: number;
  updatedAt: string;
}
const PREFIX = "yumnote.activity.v1.";
export const ACTIVITY_EVENT = "yumnote:activity";
export { activityRules };
export { workoutRules };
export type { WorkoutIntensity };

export function validActivityDate(date: string): boolean {
  const parsed = new Date(`${date}T12:00:00`);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(parsed.getTime()) && localISODate(parsed) === date && date <= localISODate();
}
export function estimateWalkingCalories(steps: number, weightKg: number | null): number | null {
  if (steps === 0) return 0;
  if (!weightKg || !Number.isFinite(weightKg) || weightKg <= 0) return null;
  return Math.round((activityRules.walkingMet - activityRules.restingMet) * weightKg * steps / activityRules.stepsPerMinute / 60);
}
export function estimateWorkoutCalories(input: { type: string; intensity: WorkoutIntensity; durationMinutes?: number | null; distanceKm?: number | null; restMinutes?: number | null; weightKg: number | null }) {
  const rule = workoutRules.find(item => item.name === input.type);
  if (!rule || !input.weightKg || !Number.isFinite(input.weightKg) || input.weightKg <= 0) return null;
  const index = input.intensity === "light" ? 0 : input.intensity === "moderate" ? 1 : 2;
  const durationMinutes = input.durationMinutes && input.durationMinutes > 0
    ? input.durationMinutes
    : input.distanceKm && input.distanceKm > 0 && rule.speedsKmh
      ? input.distanceKm / rule.speedsKmh[index] * 60 : null;
  if (!durationMinutes || !Number.isFinite(durationMinutes) || durationMinutes > activityRules.maxWorkoutMinutes) return null;
  const restMinutes = input.restMinutes ?? 0;
  if (!Number.isFinite(restMinutes) || restMinutes < 0 || restMinutes >= durationMinutes) return null;
  const activeMinutes = durationMinutes - restMinutes;
  const met = rule.mets[index];
  return { calories: Math.round((met - activityRules.restingMet) * input.weightKg * activeMinutes / 60), durationMinutes: Math.round(durationMinutes), activeMinutes: Math.round(activeMinutes), met };
}
export function workoutStepOverlap(workout: Workout): number {
  if (!workout.includedInSteps || !workout.durationMinutes) return 0;
  const rule = workoutRules.find(item => item.name === workout.type);
  const cadence = rule?.stepsPerMinute ?? (rule?.id === "walking" || rule?.id === "hiking" ? activityRules.stepsPerMinute : 0);
  return Math.round(Math.min(workout.durationMinutes, activityRules.maxWorkoutMinutes) * cadence);
}
export function uncountedSteps(steps: number, workouts: Workout[]): number {
  return Math.max(0, steps - workouts.reduce((sum, workout) => sum + workoutStepOverlap(workout), 0));
}
function walkingCaloriesForEntry(steps: number, workouts: Workout[], weightKg: number | null, manualTotal?: number): number | null {
  const remaining = uncountedSteps(steps, workouts);
  if (manualTotal !== undefined) return steps ? Math.round(manualTotal * remaining / steps) : 0;
  return estimateWalkingCalories(remaining, weightKg);
}
export function activityBudget(entry: ActivityEntry | null) {
  const burnedCalories = (entry?.walkingCalories ?? 0) + (entry?.workoutCalories ?? 0);
  return { burnedCalories, additionalCalories: burnedCalories };
}
export function loadActivity(date: string): ActivityEntry | null {
  const raw = localStorage.getItem(PREFIX + date);
  if (!raw) return null;
  const entry = JSON.parse(raw) as ActivityEntry;
  if (entry.date !== date || !Number.isInteger(entry.steps) || entry.steps < 0 || entry.steps > activityRules.maxSteps || !Number.isInteger(entry.workoutCalories) || entry.workoutCalories < 0 || entry.workoutCalories > activityRules.maxWorkoutCalories || (entry.walkingCalories !== null && (!Number.isFinite(entry.walkingCalories) || entry.walkingCalories < 0)) || (entry.walkingCaloriesInput !== undefined && (!Number.isInteger(entry.walkingCaloriesInput) || entry.walkingCaloriesInput < 0 || entry.walkingCaloriesInput > activityRules.maxWorkoutCalories))) throw new Error("Некорректная запись активности");
  if (entry.workouts !== undefined) {
    if (!Array.isArray(entry.workouts) || entry.workouts.some(workout => !workout || typeof workout.id !== "string" || !workout.id || !validWorkoutType(workout.type) || !Number.isInteger(workout.calories) || workout.calories <= 0 || (workout.durationMinutes !== undefined && (!Number.isFinite(workout.durationMinutes) || workout.durationMinutes <= 0 || workout.durationMinutes > activityRules.maxWorkoutMinutes)) || (workout.distanceKm !== undefined && (!Number.isFinite(workout.distanceKm) || workout.distanceKm <= 0 || workout.distanceKm > activityRules.maxWorkoutDistanceKm))) || new Set(entry.workouts.map(workout => workout.id)).size !== entry.workouts.length || entry.workouts.reduce((sum, workout) => sum + workout.calories, 0) !== entry.workoutCalories) throw new Error("Некорректная запись тренировок");
  }
  return entry;
}
export function saveActivity(date: string, steps: number, workoutCalories: number, weightKg: number | null): ActivityEntry {
  if (!validActivityDate(date) || !Number.isInteger(steps) || steps < 0 || steps > activityRules.maxSteps || !Number.isInteger(workoutCalories) || workoutCalories < 0 || workoutCalories > activityRules.maxWorkoutCalories) throw new Error("Проверьте дату, шаги и калории");
  const entry = { date, steps, workoutCalories, walkingCalories: estimateWalkingCalories(steps, weightKg), updatedAt: new Date().toISOString() };
  localStorage.setItem(PREFIX + date, JSON.stringify(entry));
  window.dispatchEvent(new Event(ACTIVITY_EVENT));
  return entry;
}
export function budgetForActivityDate(date: string) { return activityBudget(loadActivity(date)); }

export const workoutTypes = workoutRules.map(rule => rule.name);
export const validWorkoutType = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0 && value.length <= 100;
export interface Workout { id: string; type: string; calories: number; intensity?: WorkoutIntensity; durationMinutes?: number; distanceKm?: number; restMinutes?: number; includedInSteps?: boolean; manuallyEdited?: boolean }
export function workoutsForEntry(entry: ActivityEntry | null): Workout[] {
  return entry?.workouts ?? (entry?.workoutCalories ? [{ id: "legacy", type: "Другая тренировка", calories: entry.workoutCalories }] : []);
}
export function orderedWorkoutTypes(): string[] {
  let recent: string | null = null;
  try { recent = localStorage.getItem("yumnote.workout.last"); } catch { /* Preferences are optional. */ }
  return recent && validWorkoutType(recent) ? [recent, ...workoutTypes.filter(type => type !== recent)] : [...workoutTypes];
}
export function saveWorkouts(date: string, workouts: Workout[], recent?: string, weightKg?: number | null): ActivityEntry {
  const ids = new Set<string>();
  if (!validActivityDate(date) || workouts.some(workout => {
    if (!workout.id || ids.has(workout.id) || !validWorkoutType(workout.type) || !Number.isInteger(workout.calories) || workout.calories <= 0) return true;
    ids.add(workout.id);
    return false;
  })) throw new Error("Проверьте тренировку и калории");
  const workoutCalories = workouts.reduce((sum, workout) => sum + workout.calories, 0);
  if (workoutCalories > activityRules.maxWorkoutCalories) throw new Error("За день можно указать до 10 000 ккал тренировок");
  const previous = loadActivity(date);
  const steps = previous?.steps ?? 0;
  const previousUncounted = uncountedSteps(steps, workoutsForEntry(previous));
  const walkingCalories = previous?.walkingCaloriesInput !== undefined
    ? walkingCaloriesForEntry(steps, workouts, weightKg ?? null, previous.walkingCaloriesInput)
    : weightKg && Number.isFinite(weightKg) && weightKg > 0
      ? walkingCaloriesForEntry(steps, workouts, weightKg)
      : previous?.walkingCalories !== null && previous?.walkingCalories !== undefined && previousUncounted > 0
        ? Math.round(previous.walkingCalories * uncountedSteps(steps, workouts) / previousUncounted)
        : previous?.walkingCalories ?? (steps ? null : 0);
  const entry: ActivityEntry = { date, steps, walkingCalories, ...(previous?.walkingCaloriesInput !== undefined ? { walkingCaloriesInput: previous.walkingCaloriesInput } : {}), workouts, workoutCalories, updatedAt: new Date().toISOString() };
  localStorage.setItem(PREFIX + date, JSON.stringify(entry));
  if (recent) { try { localStorage.setItem("yumnote.workout.last", recent); } catch { /* Do not fail a saved workout for an optional preference. */ } }
  window.dispatchEvent(new Event(ACTIVITY_EVENT));
  return entry;
}

export function saveSteps(date: string, steps: number, weightKg: number | null, manualCalories?: number | null): ActivityEntry {
  if (!validActivityDate(date) || !Number.isInteger(steps) || steps < 0 || steps > activityRules.maxSteps) throw new Error("Укажите целое количество шагов от 0 до 100 000");
  if (manualCalories !== undefined && manualCalories !== null && (!Number.isInteger(manualCalories) || manualCalories < 0 || manualCalories > activityRules.maxWorkoutCalories)) throw new Error("Укажите калории от 0 до 10 000");
  const previous = loadActivity(date);
  const workouts = workoutsForEntry(previous);
  const walkingCaloriesInput = steps && manualCalories !== null ? manualCalories : undefined;
  const entry: ActivityEntry = {
    date,
    steps,
    walkingCalories: walkingCaloriesForEntry(steps, workouts, weightKg, walkingCaloriesInput),
    ...(walkingCaloriesInput !== undefined ? { walkingCaloriesInput } : {}),
    workouts,
    workoutCalories: workouts.reduce((sum, workout) => sum + workout.calories, 0),
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(PREFIX + date, JSON.stringify(entry));
  window.dispatchEvent(new Event(ACTIVITY_EVENT));
  return entry;
}
